<script lang="ts">
import { defineComponent } from 'vue';
import { fetchHealth } from '@/api/admin-be';
import type { ApiHealth } from '@/types/admin-be';
import { useStatusStore } from '@/stores/status';
import { mapState } from 'pinia';
import dayjs from '@/utils/dayjs';
import { formatBytes } from '@/utils/format';

export default defineComponent({
  name: 'status',
  computed: {
    ...mapState(useStatusStore, ['serviceStatus', 'statuses', 'rabbitStatus']),
    sortedStatuses() {
      const { showLocal } = this.$route.query;

      const localNodes = ['local', 'fe'];

      return this.statuses
        .filter(
          (s) =>
            showLocal !== undefined ||
            !localNodes.includes(s.nodes.find((n) => n)?.env || 'unknown')
        )
        .sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
    },
  },
  data() {
    return {
      health: null as ApiHealth | null,
      interval: null as null | ReturnType<typeof setTimeout>,
      now: Date.now(),
      showLocal: false,
    };
  },
  onUnmount() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    dayjs,
    fetchHealth,
    formatBytes,
    panelStyle(nodes: []) {
      return nodes.length > 1 ? 'grid-column-start: span 2' : '';
    },
  },
  mounted() {
    fetchHealth().then((data: ApiHealth) => (this.health = data));
    this.interval = setInterval(() => {
      this.now = Date.now();
    }, 500);
  },
});
</script>

<template>
  <div class="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-col-2 gap-1">
    <Panel class="shadow-lg rounded-lg m-3" v-if="rabbitStatus">
      <template #header>
        <div class="flex items-center gap-2 border-b border-b-orange-300">
          <span class="font-thin">RabbitMQ</span>
          <span class="font-thin opacity-50"> </span>
        </div>
      </template>
      <Card class="card-class flex-grow">
        <template #content>
          <div
            class="grid grid-cols-2 gap-x-3 text-gray-700 dark:text-gray-300 text-xs text-right font-mono"
          >
            <span class="text-left">msg total</span>
            <span>{{ rabbitStatus?.publish }}</span>
            <span class="text-left">queue</span>
            <span>{{ Math.max(rabbitStatus?.publish - rabbitStatus?.deliver, 0) }}</span>
          </div>
        </template>
      </Card>
    </Panel>
    <Panel class="shadow-lg rounded-lg m-3">
      <template #header>
        <div class="flex items-center gap-2 border-b border-b-orange-300">
          <span class="font-thin">PostgreSQL</span>
          <span class="font-thin opacity-50"> </span>
        </div>
      </template>
      PostgreSQL
    </Panel>
    <Panel class="shadow-lg rounded-lg m-3">
      <template #header>
        <div class="flex items-center gap-2 border-b border-b-orange-300">
          <span class="font-thin">Redis</span>
          <span class="font-thin opacity-50"> </span>
        </div>
      </template>
      Redis
    </Panel>
    <template v-for="{ name, nodes } in sortedStatuses" :key="name">
      <Panel class="shadow-lg rounded-lg m-3" :style="panelStyle(nodes as [])">
        <template #header>
          <div class="flex items-center gap-2 border-b border-b-orange-300">
            <span class="font-thin">{{ name.split('.')[0] }}</span>
            <span class="font-thin opacity-50" v-if="nodes.length">
              {{ nodes.find((n) => n)?.env }}
            </span>
          </div>
        </template>

        <template #footer>
          <div class="w-full text-right text-gray-500 pr-3 text-xs">
            <template v-if="nodes.length">
              Сервис
              <span class="font-bold font-mono">
                {{ nodes.find((n) => n)?.svc }}
              </span>
              сборка
              <span class="font-bold font-mono">
                {{ nodes.find((n) => n)?.build }}
              </span>
            </template>
            <template v-else>Ожидаем...</template>
          </div>
        </template>

        <div class="flex flex-row items-left gap-2 flex-wrap">
          <template v-for="node in nodes">
            <Card v-if="node" class="card-class flex-grow">
              <template #content>
                <div
                  class="grid grid-cols-2 gap-x-3 text-gray-700 dark:text-gray-300 text-xs text-right font-mono"
                >
                  <div class="text-left">pid</div>
                  <div>{{ node.id }}:{{ node.pid }}</div>
                  <div class="text-left">heap used</div>
                  <div class="text-nowrap">
                    {{ formatBytes(node.mem.heapUsed) }}
                  </div>
                  <div class="text-left">heap total</div>
                  <div class="text-nowrap">
                    {{ formatBytes(node.mem.heapTotal) }}
                  </div>
                  <div class="text-left">external</div>
                  <div class="text-nowrap">
                    {{ formatBytes(node.mem.external) }}
                  </div>
                  <div class="text-left">buffers</div>
                  <div class="text-nowrap">
                    {{ formatBytes(node.mem.arrayBuffers) }}
                  </div>
                  <template v-if="now - node.ts < 5000">
                    <div class="text-green-500 text-left">онлайн</div>
                    <div class="text-green-500">
                      {{ dayjs(now).locale('ru').from(node.startedTs) }}
                    </div>
                  </template>
                  <template v-else>
                    <div class="text-red-500 text-left col-span-2">оффлайн</div>
                  </template>
                </div>
              </template>
            </Card>
          </template>
        </div>
      </Panel>
    </template>
  </div>
</template>

<style lang="scss">
.p-panel {
  @apply flex flex-col;

  &-content,
  &-footer,
  &-header {
    @apply p-3;
  }

  &-content-container {
    @apply flex-grow flex flex-col;
  }

  &-content {
    @apply flex-grow;
  }

  &-header {
    @apply pb-0;
  }

  &-footer {
    @apply pt-0;
  }
}

.card-class {
  @apply shadow rounded-lg text-sm;
  @apply border border-gray-400 dark:border-gray-400;
  @apply bg-gray-100 dark:bg-gray-800;
  @apply flex-grow;
}
</style>
